import React, { useState, useEffect, useMemo } from "react";
import { collection, getDocs, query, Timestamp } from "firebase/firestore";
import { db } from "./firebase";
import { useAdmin } from "./hooks/useAdmin";

// Normalize a name for deduplication: lowercase, collapse whitespace, trim
function normalizeName(name) {
  return (name || "").toLowerCase().replace(/\s+/g, " ").trim();
}

export default function AdminJudges() {
  const { isAdmin, loading: adminLoading } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Map: normalizedName → judge entry (deduplicated by name)
  const [judgeMap, setJudgeMap] = useState({});

  // Filters / search
  const [search, setSearch] = useState("");
  const [selectedYear, setSelectedYear] = useState("all");
  const [selectedDiscipline, setSelectedDiscipline] = useState("all");
  const [expandedJudge, setExpandedJudge] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const map = {}; // normalizedName → judge entry

        const snapshot = await getDocs(query(collection(db, "competitions")));

        snapshot.forEach((docSnap) => {
          const comp = docSnap.data();
          if (!comp.adjudicators || !Array.isArray(comp.adjudicators)) return;

          const compDate =
            comp.timestamp instanceof Timestamp
              ? comp.timestamp.toDate()
              : comp.timestamp?.seconds
              ? new Date(comp.timestamp.seconds * 1000)
              : null;

          const compEntry = {
            compId: docSnap.id,
            name: comp.name || docSnap.id,
            date: compDate,
            year: compDate ? compDate.getFullYear() : null,
            discipline: comp.discipline || "—",
            category: comp.category || "—",
            dance: comp.dance || null,
            participants: comp.results ? comp.results.length : null,
          };

          comp.adjudicators.forEach((adj) => {
            if (!adj || !adj.name) return;
            const norm = normalizeName(adj.name);
            if (!norm) return;

            if (!map[norm]) {
              map[norm] = {
                displayName: (adj.name || "").trim(),
                country: (adj.country || "").trim(),
                // Track all letter codes this judge has been assigned across comps
                letterCodes: new Set(),
                // Track country name variations
                countries: new Set(),
                competitions: [],
              };
            }

            const entry = map[norm];

            // Accumulate letter codes (positional per competition, not a stable ID)
            if (adj.id && adj.id.trim()) entry.letterCodes.add(adj.id.trim());

            // Accumulate countries
            if (adj.country && adj.country.trim()) {
              entry.countries.add(adj.country.trim());
              entry.country = adj.country.trim();
            }

            // Keep the most complete display name (prefer longer/mixed-case)
            const newName = (adj.name || "").trim();
            if (newName.length > entry.displayName.length) {
              entry.displayName = newName;
            }

            // Avoid duplicate competition entries (same comp doc)
            const already = entry.competitions.some(c => c.compId === compEntry.compId);
            if (!already) {
              // Store which letter code was used in this specific competition
              entry.competitions.push({ ...compEntry, letterCodeUsed: (adj.id || "").trim() });
            }
          });
        });

        // Sort each judge's competitions by date desc
        Object.values(map).forEach((j) => {
          j.competitions.sort((a, b) => {
            if (!a.date && !b.date) return 0;
            if (!a.date) return 1;
            if (!b.date) return -1;
            return b.date - a.date;
          });
        });

        setJudgeMap(map);
      } catch (err) {
        console.error("AdminJudges load error:", err);
        setError("Failed to load judge data.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAdmin]);

  // Derived lists for filter dropdowns
  const { years, disciplines } = useMemo(() => {
    const yrs = new Set();
    const discs = new Set();
    Object.values(judgeMap).forEach((j) => {
      j.competitions.forEach((c) => {
        if (c.year) yrs.add(c.year);
        if (c.discipline) discs.add(c.discipline);
      });
    });
    return {
      years: [...yrs].sort((a, b) => b - a),
      disciplines: [...discs].sort(),
    };
  }, [judgeMap]);

  // Filtered + searched judges
  const judges = useMemo(() => {
    return Object.values(judgeMap)
      .filter((j) => {
        const q = search.toLowerCase();
        if (q) {
          const inName = j.displayName.toLowerCase().includes(q);
          const inCountry = [...j.countries].some(c => c.toLowerCase().includes(q));
          const inCode = [...j.letterCodes].some(lc => lc.toLowerCase().includes(q));
          if (!inName && !inCountry && !inCode) return false;
        }
        return true;
      })
      .map((j) => {
        const filtered = j.competitions.filter((c) => {
          if (selectedYear !== "all" && String(c.year) !== selectedYear) return false;
          if (selectedDiscipline !== "all" && c.discipline !== selectedDiscipline) return false;
          return true;
        });
        return { ...j, filteredComps: filtered };
      })
      .filter((j) => j.filteredComps.length > 0)
      .sort((a, b) => b.filteredComps.length - a.filteredComps.length);
  }, [judgeMap, search, selectedYear, selectedDiscipline]);

  const totalJudges = judges.length;
  const totalAssignments = judges.reduce((s, j) => s + j.filteredComps.length, 0);

  const fmtDate = (d) => {
    if (!d) return "—";
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  if (adminLoading) return <div style={{ padding: "2rem", textAlign: "center" }}>Loading…</div>;
  if (!isAdmin) return <div style={{ padding: "2rem", textAlign: "center" }}>🔒 Access Denied</div>;

  return (
    <div style={{ padding: "1.5rem", maxWidth: "1100px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: "1.5rem" }}>
        <h1 style={{ margin: "0 0 0.25rem", fontSize: "1.6rem" }}>👨‍⚖️ Judges Directory</h1>
        <p style={{ margin: 0, color: "#666", fontSize: "0.9rem" }}>
          All judges extracted from Contaste-uploaded competition results
        </p>
      </div>

      {/* Filters */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: "0.75rem", alignItems: "center",
        background: "#f8f9fa", border: "1px solid #e0e0e0", borderRadius: "8px",
        padding: "0.75rem 1rem", marginBottom: "1.25rem"
      }}>
        <input
          type="text"
          placeholder="Search by name, letter code, or country…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: "1 1 220px", padding: "0.45rem 0.75rem", border: "1px solid #ccc",
            borderRadius: "6px", fontSize: "0.9rem", minWidth: "180px"
          }}
        />
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
          style={{ padding: "0.45rem 0.75rem", border: "1px solid #ccc", borderRadius: "6px", fontSize: "0.9rem" }}
        >
          <option value="all">All Years</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select
          value={selectedDiscipline}
          onChange={(e) => setSelectedDiscipline(e.target.value)}
          style={{ padding: "0.45rem 0.75rem", border: "1px solid #ccc", borderRadius: "6px", fontSize: "0.9rem" }}
        >
          <option value="all">All Disciplines</option>
          {disciplines.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        {(search || selectedYear !== "all" || selectedDiscipline !== "all") && (
          <button
            onClick={() => { setSearch(""); setSelectedYear("all"); setSelectedDiscipline("all"); }}
            style={{
              padding: "0.45rem 0.9rem", border: "1px solid #ccc", borderRadius: "6px",
              background: "#fff", cursor: "pointer", fontSize: "0.85rem", color: "#555"
            }}
          >
            ✕ Clear
          </button>
        )}
        <span style={{ marginLeft: "auto", fontSize: "0.85rem", color: "#666", whiteSpace: "nowrap" }}>
          <strong>{totalJudges}</strong> judge{totalJudges !== 1 ? "s" : ""} · <strong>{totalAssignments}</strong> assignment{totalAssignments !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "#666" }}>⏳ Loading judge data…</div>
      ) : error ? (
        <div style={{ textAlign: "center", padding: "2rem", color: "#c62828" }}>{error}</div>
      ) : judges.length === 0 ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "#666" }}>No judges found matching your filters.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {judges.map((judge) => {
            const key = normalizeName(judge.displayName);
            const isExpanded = expandedJudge === key;
            const count = judge.filteredComps.length;
            const codes = [...judge.letterCodes].sort();
            const countries = [...judge.countries];
            const uniqueDisciplines = [...new Set(judge.filteredComps.map(c => c.discipline))];
            const activeYears = [...new Set(judge.filteredComps.map(c => c.year).filter(Boolean))].sort();

            return (
              <div
                key={key}
                style={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  overflow: "hidden",
                  background: "#fff",
                  boxShadow: isExpanded ? "0 2px 10px rgba(0,0,0,0.1)" : "none",
                  transition: "box-shadow 0.15s"
                }}
              >
                {/* Judge header row */}
                <div
                  onClick={() => setExpandedJudge(isExpanded ? null : key)}
                  style={{
                    display: "flex", alignItems: "center", gap: "0.75rem",
                    padding: "0.75rem 1rem", cursor: "pointer",
                    background: isExpanded ? "#f0f7ff" : "#fff",
                    userSelect: "none"
                  }}
                >
                  {/* Letter code badges (all codes used across competitions) */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "3px", maxWidth: "90px", flexShrink: 0 }}>
                    {codes.length > 0 ? codes.map(code => (
                      <span key={code} style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        minWidth: "26px", height: "26px", borderRadius: "6px",
                        background: "#3a3a5c", color: "#fff", fontWeight: "bold", fontSize: "0.78rem",
                      }}>
                        {code}
                      </span>
                    )) : (
                      <span style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        minWidth: "26px", height: "26px", borderRadius: "6px",
                        background: "#aaa", color: "#fff", fontWeight: "bold", fontSize: "0.78rem",
                      }}>?</span>
                    )}
                  </div>

                  {/* Name + country + disciplines */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: "600", fontSize: "0.95rem", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3 }}>
                      {judge.displayName || <em style={{ color: "#999" }}>Unknown</em>}
                      {countries.length > 0 && (
                        <span style={{ marginLeft: "0.5rem", fontSize: "0.8rem", color: "#888", fontWeight: "normal" }}>
                          ({countries.join(" / ")})
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: "0.78rem", color: "#888", marginTop: "2px" }}>
                      {uniqueDisciplines.join(" · ")}
                      {activeYears.length > 0 && (
                        <span style={{ marginLeft: "0.5rem", color: "#aaa" }}>
                          {activeYears.length === 1 ? activeYears[0] : `${activeYears[0]}–${activeYears[activeYears.length - 1]}`}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Competition count badge */}
                  <span style={{
                    background: "#e8f0fe", color: "#1a73e8", borderRadius: "20px",
                    padding: "3px 12px", fontSize: "0.82rem", fontWeight: "600", flexShrink: 0,
                    whiteSpace: "nowrap"
                  }}>
                    {count} comp{count !== 1 ? "s" : ""}
                  </span>

                  <span style={{ color: "#aaa", fontSize: "0.9rem", flexShrink: 0 }}>
                    {isExpanded ? "▲" : "▼"}
                  </span>
                </div>

                {/* Expanded detail panel */}
                {isExpanded && (
                  <div style={{ borderTop: "1px solid #e8f0fe" }}>
                    {/* Summary bar */}
                    <div style={{
                      display: "flex", flexWrap: "wrap", gap: "1rem 2rem",
                      padding: "0.65rem 1rem", background: "#f8fbff",
                      borderBottom: "1px solid #e8f0fe", fontSize: "0.82rem", color: "#444"
                    }}>
                      <div><strong>Name:</strong> {judge.displayName}</div>
                      {countries.length > 0 && (
                        <div><strong>Country:</strong> {countries.join(", ")}</div>
                      )}
                      {codes.length > 0 && (
                        <div style={{ display: "flex", alignItems: "center", gap: "4px", flexWrap: "wrap" }}>
                          <strong>Codes used:</strong>
                          {codes.map(c => (
                            <span key={c} style={{
                              display: "inline-block", background: "#3a3a5c", color: "#fff",
                              borderRadius: "4px", padding: "0 6px", fontWeight: "bold", fontSize: "0.78rem"
                            }}>{c}</span>
                          ))}
                          <span style={{ color: "#999", fontSize: "0.75rem" }}>(letter code is positional per competition)</span>
                        </div>
                      )}
                      <div><strong>Total:</strong> {count} competition{count !== 1 ? "s" : ""}</div>
                      <div><strong>Disciplines:</strong> {uniqueDisciplines.join(", ")}</div>
                      {activeYears.length > 0 && (
                        <div><strong>Years:</strong> {activeYears.join(", ")}</div>
                      )}
                    </div>

                    {/* Competitions table */}
                    <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.83rem", minWidth: "700px" }}>
                        <thead>
                          <tr style={{ background: "#f0f0f0" }}>
                            <th style={thStyle}>#</th>
                            <th style={thStyle}>Competition</th>
                            <th style={thStyle}>Date</th>
                            <th style={thStyle}>Discipline</th>
                            <th style={thStyle}>Category</th>
                            <th style={thStyle}>Dance</th>
                            <th style={{ ...thStyle, textAlign: "center" }}>Entries</th>
                            <th style={{ ...thStyle, textAlign: "center" }}>Code</th>
                          </tr>
                        </thead>
                        <tbody>
                          {judge.filteredComps.map((c, i) => (
                            <tr key={c.compId + i} style={{ borderBottom: "1px solid #f0f0f0", background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                              <td style={{ ...tdStyle, color: "#999", textAlign: "center" }}>{i + 1}</td>
                              <td style={{ ...tdStyle, fontWeight: "500", whiteSpace: "normal", wordBreak: "break-word", minWidth: "180px" }}>
                                {c.name}
                              </td>
                              <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{fmtDate(c.date)}</td>
                              <td style={{ ...tdStyle, whiteSpace: "normal" }}>{c.discipline}</td>
                              <td style={{ ...tdStyle, whiteSpace: "normal" }}>{c.category}</td>
                              <td style={{ ...tdStyle, whiteSpace: "normal" }}>{c.dance || "—"}</td>
                              <td style={{ ...tdStyle, textAlign: "center" }}>{c.participants != null ? c.participants : "—"}</td>
                              <td style={{ ...tdStyle, textAlign: "center" }}>
                                {c.letterCodeUsed ? (
                                  <span style={{
                                    display: "inline-block", background: "#3a3a5c", color: "#fff",
                                    borderRadius: "5px", padding: "1px 8px", fontWeight: "bold", fontSize: "0.78rem"
                                  }}>
                                    {c.letterCodeUsed}
                                  </span>
                                ) : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const thStyle = {
  padding: "7px 12px",
  textAlign: "left",
  fontWeight: "600",
  color: "#555",
  fontSize: "0.78rem",
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  whiteSpace: "nowrap",
  borderBottom: "1px solid #ddd",
};

const tdStyle = {
  padding: "7px 12px",
  color: "#333",
  verticalAlign: "top",
};
